import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenuItems, Dropdown } from '@qodo/design-system';
import { AuthContext } from 'app/auth';
import { FEATURE_FLAGS } from 'consts/features';
import { GitProvider, linkableGitProviders } from 'consts/git';
import { DOCUMENTATION_URL } from 'consts/url';
import { useSelfServeGate } from 'hooks/billing/useSelfServeGate';
import { useFeatureAccess } from 'hooks/useFeatureAccess';
import { useUser } from 'hooks/useUser';
import { IconsMap } from 'logos/providerIcons';
import { ChevronsUpDown } from 'lucide-react';
import { User } from 'network/httpClients/PlatformClient/api.schema';
import { useUserQuery } from 'network/queries';
import { ROUTES } from 'routes/consts';
import { useDeploymentConfig } from 'services/config';
import { Avatar, AvatarFallback, AvatarImage } from 'ui/avatar';
import { SidebarMenu, SidebarMenuItem, SidebarMenuButton } from 'ui/sidebar';
import { Skeleton } from 'ui/skeleton';
import {
  ProviderToClickEventMap,
  getGitProviderUsername,
  isGitProviderConnected,
} from 'utils/git';
import { getNameInitials } from 'utils/name';
import { spawn } from 'utils/translations';
import { getTierText } from 'utils/user';
import { AccountSettingsDialog } from '../../AccountSettingsDialog';
import { USER_MENU_ICON_SIZE } from '../consts';
import { GitProviderDisconnectButton } from './GitProviderDisconnectButton';

export const UserMenu = () => {
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { user, isEnterpriseUser } = useUser();
  const { data: userData, isLoading } = useUserQuery();
  const { isSelfServe } = useSelfServeGate();
  const config = useDeploymentConfig();
  const gitProviderLinking = config.features?.git_provider_linking ?? false;
  const { isEnabled: isApiKeyEnabled, isLoading: isApiKeyLoading } =
    useFeatureAccess({ featureId: FEATURE_FLAGS.API_KEYS });

  const { tierLevel } = user || {};
  const tierLevelComponent = getTierText(tierLevel);

  const spawnUserMenu = spawn('sidebar.footer.user-menu');
  const spawnGitProviders = spawn(
    'sidebar.footer.user-menu.link-git-user-account.subItems',
  );

  const onLinkYourGitAccountClick = async (provider: GitProvider) => {
    if (isGitProviderConnected(provider, userData as User)) {
      return;
    }

    const urlGetter = ProviderToClickEventMap[provider];

    if (!urlGetter) {
      console.error(`No URL getter found for provider: ${provider}`);

      return;
    }

    const url = await urlGetter();

    if (url) {
      window.location.href = url;
    }
  };

  const onAccountSettingsClick = () => {
    setIsSettingsDialogOpen(true);
  };

  const onCliApiKeyClick = () => {
    navigate(ROUTES.API_KEYS);
  };

  const onPlansClick = () => {
    navigate(ROUTES.PLANS);
  };

  const onSignoutClick = () => {
    authContext.signout();
  };

  const formatGitProvider = (provider: GitProvider) => {
    const Icon = IconsMap[provider];
    const isConnected = isGitProviderConnected(provider, userData as User);
    const connectionStatus = isConnected
      ? '@' + getGitProviderUsername(provider, userData as User)
      : 'Link accounts';

    return (
      <div className="flex items-center gap-2">
        <Icon size={USER_MENU_ICON_SIZE} />
        <span>{spawnGitProviders(provider)} - </span>
        <span className="text-purple-300 ml-auto">{connectionStatus}</span>
        {isConnected && <GitProviderDisconnectButton provider={provider} />}
      </div>
    );
  };

  const createSubItem = (provider: GitProvider) => ({
    type: 'ActionMenuItem' as const,
    label: formatGitProvider(provider),
    onClick: () => onLinkYourGitAccountClick(provider),
  });

  const menuItems: DropdownMenuItems = [
    {
      type: 'ActionMenuItem',
      label: (
        <>
          {spawnUserMenu('account-settings')} ({tierLevelComponent})
        </>
      ),
      onClick: onAccountSettingsClick,
    },
    // Conditionally add Git Linking submenu (hidden on Single Tenant)
    ...(gitProviderLinking
      ? [
          {
            type: 'SubMenuItem' as const,
            label: spawnUserMenu('link-git-user-account'),
            subItems: linkableGitProviders.map(createSubItem),
          },
        ]
      : []),
    {
      type: 'ActionMenuItem',
      label: spawnUserMenu('cli-api-key'),
      onClick: onCliApiKeyClick,
      isHidden: isApiKeyLoading || !isApiKeyEnabled,
    },
    {
      type: 'ActionMenuItem',
      label: spawnUserMenu('plans'),
      onClick: onPlansClick,
      isHidden: isSelfServe || isEnterpriseUser,
    },
    {
      type: 'Separator',
    },
    {
      type: 'ActionMenuItem',
      label: (
        <a href={DOCUMENTATION_URL} target="_blank" rel="noreferrer">
          {spawnUserMenu('documentation')}
        </a>
      ),
    },
    {
      type: 'ActionMenuItem',
      label: spawnUserMenu('logout'),
      onClick: onSignoutClick,
    },
  ];

  if (isLoading || !userData) {
    return <Skeleton className="size-10 rounded-lg" />;
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <Dropdown
            side={'top'}
            modal
            asChild
            triggerElement={
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="size-8 rounded-lg">
                  <AvatarImage
                    src={userData?.avatar || ''}
                    alt={userData?.name}
                  />
                  <AvatarFallback className="rounded-lg bg-fill-action-emphasis-idle text-text-action-on-emphasis-idle">
                    {getNameInitials(userData.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate text-text-general-neutral-strong">
                    {userData.name}
                  </span>
                  <span className="truncate text-xs text-text-general-neutral-mid">
                    {userData.email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            }
            menuItems={menuItems}
          />
        </SidebarMenuItem>
      </SidebarMenu>

      <AccountSettingsDialog
        open={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
      />
    </>
  );
};
